import { isBugBountyParticipant } from '@atlassian/amkt-common';
import {
  ButtonClickedEvent,
  ErrorStateEvent,
  ScreenViewedEvent,
  SortClickedEvent,
  TileClickedEvent,
  TilesRenderedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import { AtlassianProductHostingType, CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { useCallback, useEffect, useRef } from 'react';

import { Analytics } from '../constants/analytics';

import type { ApolloError } from '@apollo/client';
import type { CollectionFragment } from '@atlassian/mpac-services-client';

export const isAppCloudSecurityParticipant = (app) =>
  isBugBountyParticipant(AtlassianProductHostingType.Cloud, app?.programs?.bugBountyParticipant) &&
  app.deployments.includes('cloud');
export const isAppCloudFortified = (app) =>
  app.programs?.cloudFortified?.status === CloudFortifiedStatusValues.Approved;

// eslint-disable-next-line max-lines-per-function
export const useCollectionPageAnalytics = ({
  slug,
  initialFetchError,
  collection,
  fetchMoreError,
  appsCount,
  sortKey,
  isLoading,
  isLoadingMore,
}: {
  slug?: string | null;
  initialFetchError?: ApolloError;
  collection: CollectionFragment | null;
  fetchMoreError?: ApolloError | null;
  appsCount: number;
  sortKey: string;
  isLoading: boolean;
  isLoadingMore: boolean;
}) => {
  const fireEvent = useAnalyticsEvents();
  const screenViewedRef = useRef(false);

  const fireScreenViewEvent = useCallback(() => {
    if (collection && !screenViewedRef.current) {
      screenViewedRef.current = true;
      const attributes = {
        collectionType: collection.slug,
        showsUseCaseSection: Boolean(collection.useCases),
      };

      fireEvent({
        eventType: ScreenViewedEvent,
        actionSubjectId: Analytics.subjectId.screenRendered,
        attributes,
      });
    }
  }, [collection, fireEvent]);

  useEffect(() => {
    if (isLoading) return;
    fireScreenViewEvent();
  }, [fireScreenViewEvent, isLoading]);

  const fireAppTilesEvent = useCallback(() => {
    if (appsCount >= 0 && !initialFetchError && !fetchMoreError) {
      const attributes = {
        collectionType: slug,
        filters: { sort: sortKey },
        count: appsCount,
      };

      fireEvent({
        eventType: TilesRenderedEvent,
        actionSubjectId: Analytics.subjectId.appTilesRendered,
        attributes,
      });
    }
  }, [appsCount, fetchMoreError, initialFetchError, slug, sortKey]);

  const fireAppErrorEvent = useCallback(() => {
    if (!slug || initialFetchError || fetchMoreError) {
      const errorType = !slug
        ? Analytics.event.globalError
        : initialFetchError
        ? Analytics.event.initialError
        : Analytics.event.loadMoreError;
      const actionSubjectId = !slug
        ? Analytics.subjectId.screenRendered
        : Analytics.subjectId.appTilesRendered;

      const attributes = {
        collectionType: slug,
        errorType,
      };

      fireEvent({
        eventType: ErrorStateEvent,
        actionSubjectId,
        attributes,
      });
    }
  }, [fetchMoreError, initialFetchError, slug]);

  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    fireAppTilesEvent();
  }, [fireAppTilesEvent, isLoading, isLoadingMore]);

  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    fireAppErrorEvent();
  }, [fireAppErrorEvent, isLoading, isLoadingMore]);

  const fireOnTileClickEvent = (attributes, app) => {
    const allAttributes = {
      ...attributes,
      label: app.addonKey,
      appName: app.name,
      currentPerspective: 'collection',
      hitSize: 'large',
      isPaidApp: app?.version?.paid,
      productKeys: app?.version?.compatibilities.map((appData) => appData.applicationName),
      hasCloudSecurityBadge: isAppCloudSecurityParticipant(app),
      hasCloudFortifiedBadge: isAppCloudFortified(app),
    };

    fireEvent({
      eventType: TileClickedEvent,
      actionSubjectId: Analytics.subjectId.appTileClicked,
      attributes: allAttributes,
    });
  };

  const fireOnLoadMoreClickEvent = (attributes) => {
    fireEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: Analytics.subjectId.loadMoreClicked,
      attributes,
    });
  };

  const fireOnSortOptionSelectEvent = (attributes) => {
    fireEvent({
      eventType: SortClickedEvent,
      actionSubjectId: Analytics.subjectId.sortOptionSelected,
      attributes,
    });
  };

  return { fireOnTileClickEvent, fireOnLoadMoreClickEvent, fireOnSortOptionSelectEvent };
};
